import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { ReportType } from '@repo/shared/types';
import { Button } from '@repo/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@repo/ui/card';
import { cn } from '@repo/ui/lib/utils';
import { Building2, CheckCircle2, Loader2, User } from 'lucide-react';
import { OLink } from '../organization-link';

export const SelectReportType = ({
  selectedReportType,
  setSelectedReportType,
  onContinue,
  isPending,
}: {
  selectedReportType: ReportType;
  setSelectedReportType: (type: ReportType) => void;
  onContinue: () => void;
  isPending: boolean;
}) => {
  useLocale();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">{t('reports.newReport')}</CardTitle>
        <CardDescription>{t('reports.selectTheTypeOfDueDiligenceReviewToBegin')} </CardDescription>
      </CardHeader>

      <CardContent>
        <div aria-labelledby="step1-review-type-heading" className="space-y-4" role="group">
          <div aria-label={t('reports.reviewType')} className="space-y-3" role="radiogroup">
            <label
              aria-label={t('reports.individualKycReviewForIndividualClients')}
              className={cn(
                'flex cursor-pointer items-start gap-4 rounded-lg border-2 p-4 transition-all',
                selectedReportType === ReportType.KYC
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50',
              )}
            >
              <div className="flex flex-1 items-center gap-3">
                <div
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
                    selectedReportType === ReportType.KYC
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground',
                  )}
                >
                  {selectedReportType === ReportType.KYC && (
                    <CheckCircle2 className="h-4 w-4 text-primary-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">{t('reports.individual')}</span>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {t('reports.reviewForIndividualClientsKyc')}{' '}
                  </p>
                </div>
              </div>
              <input
                checked={selectedReportType === ReportType.KYC}
                className="sr-only"
                name="reviewType"
                onChange={() => setSelectedReportType(ReportType.KYC)}
                type="radio"
                value="KYC"
              />
            </label>

            <label
              aria-label={t('reports.businessKybReviewForBusinessClientsAndKeyIndividuals')}
              className={cn(
                'flex cursor-pointer items-start gap-4 rounded-lg border-2 p-4 transition-all',
                selectedReportType === ReportType.KYB
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50',
              )}
            >
              <div className="flex flex-1 items-center gap-3">
                <div
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
                    selectedReportType === ReportType.KYB
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground',
                  )}
                >
                  {selectedReportType === ReportType.KYB && (
                    <CheckCircle2 className="h-4 w-4 text-primary-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">{t('reports.business')}</span>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {t('reports.reviewForBusinessClientsIncludingTheBusinessEntityAnd')}{' '}
                  </p>
                </div>
              </div>
              <input
                checked={selectedReportType === ReportType.KYB}
                className="sr-only"
                name="reviewType"
                onChange={() => setSelectedReportType(ReportType.KYB)}
                type="radio"
                value="KYB"
              />
            </label>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex items-center justify-between gap-3 border-t pt-6">
        <Button asChild disabled={isPending} variant="outline">
          <OLink to="/reports">{t('reports.cancel')}</OLink>
        </Button>

        <Button disabled={!selectedReportType || isPending} onClick={onContinue}>
          {isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('reports.creating')}{' '}
            </>
          ) : (
            t('reports.startDueDiligence')
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};
